import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CreatingModelComment } from '../domain';
import { useTextSelectable } from '@view-model/models/common/hooks';
import { DraggableContainer } from '@view-model/models/common/components/DraggableContainer';
import { ForeignObjectAutosize2 } from '@view-model/models/common/components/ForeignObjectAutosize';
import { CommentConstants } from '../constants';
import { ModelCommentBodyForm } from './ModelCommentBodyForm';
import { Button } from '@framework/ui/atoms/Button';
import type { DragContext } from '@model-framework/ui';
import { useCurrentUserPublicProfile } from '@user/PublicProfile/useUserPublicProfile';
import { generateDefaultUserIcon } from '@framework/utils';
import { Size } from '@view-model/models/common/basic';

type Props = {
  creatingComment: CreatingModelComment;
  onDrag(dx: number, dy: number): void;
  onDragEnd(): void;
  onSubmit(creatingComment: CreatingModelComment): void;
  onCancel(): void;
};

const AVATAR_SIZE = 36;
const AVATAR_PADDING = 6;
const DRAG_HANDLE_HEIGHT = 24;

export const CreatingModelCommentView: React.FC<Props> = ({
  creatingComment,
  onDrag,
  onDragEnd,
  onSubmit,
  onCancel,
}: Props) => {
  const { t } = useTranslation();
  const ref = useRef(null);
  // マウスイベントが伝搬することでテキスト操作（選択、リンククリック）ができず、ドラッグによりキャンバス自体が移動してしまうのを止める
  useTextSelectable(ref);
  const [body, setBody] = useState<string>(creatingComment.body);
  const [containerSize, setContainerSize] = useState<Size>(Size.empty);
  const profile = useCurrentUserPublicProfile();
  const { position } = creatingComment;

  const { width } = CommentConstants.UISetting;

  const handleDrag = useCallback(
    ({ dx, dy }: DragContext) => {
      onDrag(dx, dy);
    },
    [onDrag]
  );

  const handleDragEnd = useCallback(() => {
    onDragEnd();
  }, [onDragEnd]);

  const handleSubmit = useCallback(() => {
    onSubmit(creatingComment.withBody(body));
  }, [body, creatingComment, onSubmit]);

  const handleCancel = useCallback(() => {
    onCancel();
  }, [onCancel]);

  const handleChangeContainerSize = useCallback((size: Size) => {
    setContainerSize(size.addHeight(DRAG_HANDLE_HEIGHT));
  }, []);

  if (!profile) return null;

  return (
    <DraggableContainer
      position={position}
      onDrag={handleDrag}
      onDragEnd={handleDragEnd}
      dragHandleContent={
        // グリップストリップ: フォーム上部のドラッグハンドル
        <foreignObject width={width} height={DRAG_HANDLE_HEIGHT} style={{ cursor: 'move' }}>
          <div className="flex size-full items-center justify-center rounded-t-2xl bg-white">
            <div className="h-1.5 w-12 rounded-full bg-gray-300" />
          </div>
        </foreignObject>
      }
    >
      {/* 背景 rect（グリップ＋コンテンツを包む外枠） */}
      <rect
        width={containerSize.width}
        height={containerSize.height}
        fill="white"
        stroke="#d1d5db"
        strokeWidth="2"
        rx="16"
        ry="16"
      />

      {/* グリップ部分の高さ分だけ y軸方向にオフセットする */}
      <g ref={ref} transform={`translate(0, ${DRAG_HANDLE_HEIGHT})`}>
        <ForeignObjectAutosize2 onSizeChange={handleChangeContainerSize}>
          <div className="rounded-b-2xl bg-white p-4 pb-3 text-3xl" style={{ width: width }}>
            {/* ユーザー情報 */}
            <div className="mb-2 flex items-center">
              <div
                style={{
                  padding: AVATAR_PADDING,
                  width: AVATAR_SIZE + AVATAR_PADDING * 2,
                  height: AVATAR_SIZE + AVATAR_PADDING * 2,
                }}
              >
                {profile.imageUrl ? (
                  <img
                    className="max-w-none rounded-full bg-neutral-300 object-cover"
                    src={profile.imageUrl}
                    alt={profile.name}
                    style={{ width: AVATAR_SIZE, height: AVATAR_SIZE }}
                  />
                ) : (
                  <div
                    className="max-w-none overflow-hidden rounded-full bg-neutral-300 object-cover"
                    style={{ width: AVATAR_SIZE, height: AVATAR_SIZE }}
                  >
                    {generateDefaultUserIcon({
                      id: profile.id,
                      name: profile.name,
                      isAnonymous: profile.isAnonymous,
                      size: AVATAR_SIZE,
                    })}
                  </div>
                )}
              </div>
              <div className="overflow-hidden text-ellipsis pl-[6px] text-3xl font-bold">{profile.name}</div>
            </div>
            <ModelCommentBodyForm
              body={body}
              ariaLabel={t('viewModel.toolbar.addComment')}
              onChange={(body) => setBody(body)}
              onSubmit={handleSubmit}
              minRows={CommentConstants.UISetting.formMinRows}
            />
            <div className="flex justify-end gap-2">
              <Button color="mono" className="rounded-lg p-2 text-3xl" onClick={handleCancel}>
                {t('common.action.close')}
              </Button>
              <Button color="brand" className="rounded-lg p-2 text-3xl" onClick={handleSubmit} disabled={!body}>
                {t('common.action.send')}
              </Button>
            </div>
          </div>
        </ForeignObjectAutosize2>
      </g>
    </DraggableContainer>
  );
};
